// @flow
import React, { useMemo } from "react";
import { useAsyncMemo, useAbortController } from "hooks";
import { fetchBreadcrumb } from "data/api";
import Icons from "@hs/icons";
import queryString from "data/queryString";
import Breadcrumb from "components/Breadcrumb";
import { getRoute } from "components/ItemUriLink";
import AttributeLabel from "components/ExtraAttributes/AttributeLabel";
import ViewerControlBase from "components/ExtraAttributes/ViewerControl/ViewerControlBase";
import styles from "./LinkViewer.module.css";
import sharedStyles from "shared/styles.module.css";
import { getClassNames, getLocalizedText } from "data/utils";

type Props = {
    /** attribute name */
    name: string,
    /** current value */
    value: string | any,
    /** which link type to create (mailto, tel, ...) set as prefix (if not defined will show localized "open link" text */
    linkType?: string,
    /** value width */
    width?: number,
    /** which FontAwesome icon to render */
    icon?: string,
    /** whether icon should be place at beginning or end of text */
    isIconLast?: boolean,
    width?: number,
    className?: string,
    caption?: string,
    /** optional onClick handler */
    onClick?: () => void,
    /** whether to render the detailed view or not */
    renderDetails?: boolean,
};

type ControlledProps = Props & {
    /** label text */
    header?: string,
    /** whether rendered inside a Grid */
    isGridCell: boolean,
};

const renderValue = (
    linkType?: string,
    value?: string,
    caption?: string,
    icon?: string,
    isIconLast?: boolean
) => (
    <>
        {icon && isIconLast === false && (
            <>
                <Icons.Library name={icon} />{" "}
            </>
        )}
        {linkType == null && caption == null ? (
            <span>{getLocalizedText("$plugin:extraAttributes.link_open")}</span>
        ) : (
            <span>{caption || value}</span>
        )}
        {icon && isIconLast && (
            <>
                {" "}
                <Icons.Library name={icon} />
            </>
        )}
    </>
);

const DetailedValue = ({ className, href, value }) => {
    let comparableValue = value.toLowerCase();
    const abortController = useAbortController();
    const breadcrumb = useAsyncMemo(async () => {
        let siteType = 1;
        let icon = "globe";
        const parts = [];
        if (
            !(
                comparableValue.startsWith("inpoint:browse") ||
                comparableValue.startsWith("pam-item://")
            )
        ) {
            let parsedUrl = null;
            try {
                parsedUrl = new URL(href);
            } catch {
                console.warn(
                    "LinkViewerControl: invalid URL, using default text"
                );
                return {
                    isReadonly: false,
                    icon,
                    siteType,
                    parts: [
                        {
                            text: getLocalizedText(
                                "$plugin:extraAttributes.link_open"
                            ),
                            itemUri: href,
                        },
                    ],
                };
            }
            return {
                isReadonly: false,
                icon,
                siteType,
                parts: [{ text: parsedUrl.hostname, itemUri: href }],
            };
        }
        let itemUri = value;
        if (comparableValue.startsWith("inpoint:browse")) {
            const qs = queryString.parse(value.substr(value.indexOf("?")));
            itemUri = qs["uri"];
        }
        return {
            ...(await fetchBreadcrumb(itemUri, abortController(true).signal)),
            isReadonly: false,
        };
    }, [value]);
    return (
        breadcrumb && (
            <div className={className}>
                <Breadcrumb
                    isReadonly={breadcrumb.isReadonly}
                    siteType={breadcrumb.siteType}
                    parts={breadcrumb.parts}
                    icon={breadcrumb.icon}
                    leafActive={true}
                />
            </div>
        )
    );
};

const useAnchorProps = (value, linkType, width, isDoc) => {
    // #35977 convert inpoint:browse links
    let href = String(value || "");
    let target = "_blank";

    let comparableValue = href.toLowerCase();
    if (comparableValue.startsWith("inpoint:browse")) {
        const qs = queryString.parse(value.substr(value.indexOf("?")));
        /* istanbul ignore else */
        if (qs["uri"] !== undefined) {
            target = "_self";
            href = getRoute({
                itemUri: qs.uri,
                isExternalLink: true,
                isDoc,
            });
        }
    } else if (comparableValue.startsWith("pam-item://")) {
        if (!isDoc) {
            target = "_self";
        }
        href = getRoute({
            itemUri: href,
            isExternalLink: true,
            isDoc,
        });
    } else if (linkType != null) {
        href = `${linkType}:${href}`;
    }
    let style: CSSStyleDeclaration = useMemo(
        () => ({
            color: "#337ab7",
            width: width && width > 0 ? `${width}px` : "100px",
        }),
        [width]
    );
    return useMemo(() => ({ href, target, style }), [href, target, style]);
};

export const LinkViewer = ({
    name,
    value,
    isDoc,
    linkType,
    width,
    icon,
    isIconLast,
    className,
    caption,
    onClick,
    target: targetFromProps,
    title,
    renderDetails = false,
}: Props) => {
    const { style, href, target } = useAnchorProps(
        value,
        linkType,
        width,
        isDoc
    );

    // if there's no value for mail fields, don't display the icon or the mailto link
    if (!value && linkType === "mailto") {
        return <span className={className}></span>;
    }

    return href.length > 0 ? (
        <>
            {renderDetails ? (
                <DetailedValue
                    href={href}
                    value={value}
                    className={className}
                />
            ) : (
                <a
                    className={className}
                    href={href}
                    style={style}
                    target={targetFromProps ?? target}
                    onClick={onClick}
                    rel="noopener noreferrer"
                    title={title}
                >
                    {renderValue(linkType, value, caption, icon, isIconLast)}
                </a>
            )}
        </>
    ) : (
        <span
            className={getClassNames(
                className,
                styles.nolink,
                sharedStyles.select_none,
                sharedStyles.cursor_forbidden
            )}
        >
            {getLocalizedText("$plugin:extraAttributes.link_empty")}
        </span>
    );
};
LinkViewer.displayName = "LinkViewer";

const LinkViewerControl = ({
    header,
    isGridCell = false,
    isIconLast = true,
    icon = "fa-regular fa-arrow-up-right-from-square",
    ...props
}: ControlledProps) => {
    if (isGridCell)
        return (
            <div style={{ textAlign: "center" }}>
                <LinkViewer isIconLast={isIconLast} icon={icon} {...props} />
            </div>
        );
    else
        return (
            <ViewerControlBase>
                <AttributeLabel label={header} />{" "}
                <LinkViewer
                    className={`form-control form-control-static ${sharedStyles.select_text}`}
                    isIconLast={isIconLast}
                    icon={icon}
                    renderDetails={true}
                    {...props}
                />
            </ViewerControlBase>
        );
};

LinkViewerControl.displayName = "LinkViewerControl";
export default LinkViewerControl;
